/**
 * Shared listing UI for VHP collection and blog catalog sections:
 * load-more, view toggle, mobile filter drawer, section replace (filters).
 */
(function () {
  const initVhpListingSection = (root, options = {}) => {
    if (!root) return;

    const mode = options.mode || root.dataset.listingMode || 'collection';
    const sectionId = root.dataset.sectionId;
    const sectionSelector = `#${root.id}`;
    const listingBaseUrl =
      root.dataset.listingUrl || root.dataset.collectionUrl || window.location.pathname;
    const viewKey = `vhp-listing-view-${sectionId}`;
    const mobileFiltersKey = `vhp-listing-mobile-filters-open-${sectionId}`;
    const form = root.querySelector(`#vhpCollectionFilters-${sectionId}`);

    let isLoading = false;

    const setLoading = (active, loadingMode = '') => {
      const current = document.querySelector(sectionSelector);
      if (!current) return;
      current.classList.toggle('is-loading', active);
      current.classList.toggle('is-loading-append', active && loadingMode === 'append');

      const loadMoreBtn = current.querySelector('#loadMoreBtn');
      if (loadMoreBtn instanceof HTMLButtonElement) {
        if (active && loadingMode === 'append') {
          loadMoreBtn.disabled = true;
          loadMoreBtn.classList.add('is-loading');
          loadMoreBtn.textContent = window.vhpT('listing.loading');
        } else {
          loadMoreBtn.disabled = false;
          loadMoreBtn.classList.remove('is-loading');
          loadMoreBtn.textContent = window.vhpT('listing.load_more');
        }
      }
    };

    const applySavedView = () => {
      const current = document.querySelector(sectionSelector);
      if (!current) return;
      const grid = current.querySelector('#recipeGrid');
      const gridBtn = current.querySelector('#gridViewBtn');
      const listBtn = current.querySelector('#listViewBtn');
      const saved = sessionStorage.getItem(viewKey) || 'grid';
      if (!grid || !gridBtn || !listBtn) return;
      if (saved === 'list') {
        grid.classList.add('list-view');
        listBtn.classList.add('active');
        gridBtn.classList.remove('active');
      } else {
        grid.classList.remove('list-view');
        gridBtn.classList.add('active');
        listBtn.classList.remove('active');
      }
    };

    const buildUrlFromForm = (sourceForm) => {
      const url = new URL(listingBaseUrl, window.location.origin);
      const fd = new FormData(sourceForm);
      for (const [key, value] of fd.entries()) {
        if (value !== '') {
          url.searchParams.append(key, value);
        }
      }
      url.searchParams.delete('page');
      return url;
    };

    const getSectionUrl = (url) => {
      const sectionUrl = new URL(url.toString(), window.location.origin);
      sectionUrl.searchParams.set('section_id', sectionId);
      return sectionUrl.toString();
    };

    const parseSectionFromHTML = (html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector(sectionSelector);
    };

    const replaceSectionFromUrl = async (url) => {
      if (isLoading) return;
      isLoading = true;
      setLoading(true, 'replace');
      try {
        const response = await fetch(getSectionUrl(url));
        if (!response.ok) return;
        const html = await response.text();
        const incoming = parseSectionFromHTML(html);
        const current = document.querySelector(sectionSelector);
        if (!incoming || !current) return;
        current.innerHTML = incoming.innerHTML;
        history.pushState({}, '', url.toString());
        bindSectionEvents();
        applySavedView();
      } finally {
        isLoading = false;
        setLoading(false);
      }
    };

    const appendNextPage = async (nextRelativeUrl) => {
      if (isLoading || !nextRelativeUrl) return;
      isLoading = true;
      setLoading(true, 'append');
      try {
        const nextUrl = new URL(nextRelativeUrl, window.location.origin);
        const response = await fetch(getSectionUrl(nextUrl));
        if (!response.ok) return;
        const html = await response.text();
        const incoming = parseSectionFromHTML(html);
        const current = document.querySelector(sectionSelector);
        if (!incoming || !current) return;

        const incomingGrid = incoming.querySelector('#recipeGrid');
        const currentGrid = current.querySelector('#recipeGrid');
        if (incomingGrid && currentGrid) {
          currentGrid.insertAdjacentHTML('beforeend', incomingGrid.innerHTML);
        }

        const incomingLoadWrap = incoming.querySelector('#loadMoreWrap');
        const currentLoadWrap = current.querySelector('#loadMoreWrap');
        if (incomingLoadWrap && currentLoadWrap) {
          currentLoadWrap.innerHTML = incomingLoadWrap.innerHTML;
          if (incomingLoadWrap.style.display === 'none') {
            currentLoadWrap.style.display = 'none';
          } else {
            currentLoadWrap.style.display = '';
          }
        } else if (currentLoadWrap) {
          currentLoadWrap.style.display = 'none';
        }

        const incomingShownCount = incoming.querySelector('#shownCount');
        const currentShownCount = current.querySelector('#shownCount');
        if (incomingShownCount && currentShownCount) {
          currentShownCount.textContent = incomingShownCount.textContent;
        }

        const incomingProgressFill = incoming.querySelector('#progressFill');
        const currentProgressFill = current.querySelector('#progressFill');
        if (incomingProgressFill && currentProgressFill) {
          currentProgressFill.style.width = incomingProgressFill.style.width;
        }

        const incomingResultCount = incoming.querySelector('#resultCount');
        const currentResultCount = current.querySelector('#resultCount');
        if (incomingResultCount && currentResultCount) {
          currentResultCount.textContent = incomingResultCount.textContent;
        }

        bindSectionEvents();
        applySavedView();
      } finally {
        isLoading = false;
        setLoading(false);
      }
    };

    const parseNumericValue = (raw) => {
      if (typeof raw !== 'string') return null;
      const normalized = raw.trim().replace(',', '.').replace(/[^\d.-]/g, '');
      if (!normalized) return null;
      const value = Number(normalized);
      return Number.isFinite(value) ? value : null;
    };

    const updateRangeSliderUI = (ctx) => {
      const minValue = Number(ctx.minInput.value);
      const maxValue = Number(ctx.maxInput.value);
      const total = ctx.max - ctx.min || 1;
      const start = ((Math.min(minValue, maxValue) - ctx.min) / total) * 100;
      const end = ((Math.max(minValue, maxValue) - ctx.min) / total) * 100;
      const shownMin = Math.min(minValue, maxValue);
      const shownMax = Math.max(minValue, maxValue);
      const active = document.activeElement;
      if (active !== ctx.valueMin) ctx.valueMin.value = String(shownMin);
      if (active !== ctx.valueMax) ctx.valueMax.value = String(shownMax);
      ctx.trackFill.style.left = `${start}%`;
      ctx.trackFill.style.width = `${Math.max(end - start, 0)}%`;
    };

    const buildNumericRangeFilter = (sectionEl) => {
      const list = sectionEl.querySelector('[data-filter-list]');
      if (!list) return;

      const checkboxes = Array.from(list.querySelectorAll('.sf-input')).filter(
        (el) => el instanceof HTMLInputElement && !el.disabled
      );
      if (checkboxes.length < 2) {
        sectionEl.classList.add('hidden');
        return;
      }

      const valuesWithNumbers = checkboxes.map((input) => {
        const sourceValue = input.dataset.filterNumber || input.value || '';
        return {
          input,
          number: parseNumericValue(sourceValue),
        };
      });

      if (valuesWithNumbers.some((entry) => entry.number === null)) return;

      const uniqueNumbers = Array.from(new Set(valuesWithNumbers.map((entry) => Number(entry.number)))).sort(
        (a, b) => a - b
      );
      if (uniqueNumbers.length < 2) return;

      const min = uniqueNumbers[0];
      const max = uniqueNumbers[uniqueNumbers.length - 1];
      const positiveDiffs = [];
      for (let i = 1; i < uniqueNumbers.length; i += 1) {
        const diff = uniqueNumbers[i] - uniqueNumbers[i - 1];
        if (diff > 0) positiveDiffs.push(diff);
      }
      const step = positiveDiffs.length > 0 ? Math.min(...positiveDiffs) : 1;

      const checkedValues = valuesWithNumbers
        .filter((entry) => entry.input.checked)
        .map((entry) => Number(entry.number));
      const initialMin = checkedValues.length ? Math.min(...checkedValues) : min;
      const initialMax = checkedValues.length ? Math.max(...checkedValues) : max;

      list.classList.add('sf-list--hidden');

      const rangeMarkup = document.createElement('div');
      rangeMarkup.className = 'sf-decimal-range';
      rangeMarkup.innerHTML = `
        <div class="sf-range-label-row">
          <span class="sf-range-val sf-range-val--pair">
            <input type="number" class="sf-range-number-input" data-range-min min="${min}" max="${max}" step="${step}" value="${initialMin}" aria-label="${window.vhpT('listing.from')}" inputmode="decimal">
            <span class="sf-range-val-sep" aria-hidden="true">-</span>
            <input type="number" class="sf-range-number-input" data-range-max min="${min}" max="${max}" step="${step}" value="${initialMax}" aria-label="${window.vhpT('listing.to')}" inputmode="decimal">
          </span>
        </div>
        <div class="sf-double-range">
          <div class="sf-double-range-track"></div>
          <div class="sf-double-range-fill" data-range-fill></div>
          <input type="range" class="sf-range-input sf-range-input-min" min="${min}" max="${max}" step="${step}" value="${initialMin}" data-range-min-input>
          <input type="range" class="sf-range-input sf-range-input-max" min="${min}" max="${max}" step="${step}" value="${initialMax}" data-range-max-input>
        </div>
      `;
      sectionEl.appendChild(rangeMarkup);

      const minInput = rangeMarkup.querySelector('[data-range-min-input]');
      const maxInput = rangeMarkup.querySelector('[data-range-max-input]');
      const valueMin = rangeMarkup.querySelector('input[data-range-min]');
      const valueMax = rangeMarkup.querySelector('input[data-range-max]');
      const trackFill = rangeMarkup.querySelector('[data-range-fill]');
      if (!minInput || !maxInput || !valueMin || !valueMax || !trackFill) return;

      const ctx = { minInput, maxInput, valueMin, valueMax, trackFill, min, max, step };

      const parseRangeField = (el) => {
        const raw = el instanceof HTMLInputElement ? el.value : '';
        return parseNumericValue(raw);
      };

      const clampSnap = (v) => {
        const s = Number.isFinite(step) && step > 0 ? step : 1;
        const x = Math.min(max, Math.max(min, v));
        const snapped = min + Math.round((x - min) / s) * s;
        return Math.min(max, Math.max(min, snapped));
      };

      const normalizeBounds = () => {
        const minVal = Number(minInput.value);
        const maxVal = Number(maxInput.value);
        const gap = Number.isFinite(step) && step > 0 ? step : 1;
        const activeEl = document.activeElement;

        if (activeEl === minInput || activeEl === valueMin) {
          const clamped = Math.min(minVal, maxVal - gap);
          if (clamped !== minVal) minInput.value = String(clamped);
        } else if (activeEl === maxInput || activeEl === valueMax) {
          const clamped = Math.max(maxVal, minVal + gap);
          if (clamped !== maxVal) maxInput.value = String(clamped);
        } else if (minVal > maxVal - gap) {
          minInput.value = String(maxVal - gap);
        }
        updateRangeSliderUI(ctx);
      };

      const applyTextFieldsToSliders = () => {
        const rawMin = parseRangeField(valueMin);
        const rawMax = parseRangeField(valueMax);
        if (rawMin === null && rawMax === null) return;
        const a = rawMin !== null ? clampSnap(rawMin) : Number(minInput.value);
        const b = rawMax !== null ? clampSnap(rawMax) : Number(maxInput.value);
        minInput.value = String(a);
        maxInput.value = String(b);
        normalizeBounds();
      };

      updateRangeSliderUI(ctx);

      const commitToCheckboxes = () => {
        const selectedMin = Math.min(Number(minInput.value), Number(maxInput.value));
        const selectedMax = Math.max(Number(minInput.value), Number(maxInput.value));
        valuesWithNumbers.forEach((entry) => {
          const value = Number(entry.number);
          entry.input.checked = value >= selectedMin && value <= selectedMax;
        });
        if (form) {
          replaceSectionFromUrl(buildUrlFromForm(form));
        }
      };

      const onTextFieldChange = () => {
        const rawMin = parseRangeField(valueMin);
        const rawMax = parseRangeField(valueMax);
        if (rawMin === null && rawMax === null) {
          updateRangeSliderUI(ctx);
          return;
        }
        applyTextFieldsToSliders();
        commitToCheckboxes();
      };

      minInput.addEventListener('input', normalizeBounds);
      maxInput.addEventListener('input', normalizeBounds);
      minInput.addEventListener('change', commitToCheckboxes);
      maxInput.addEventListener('change', commitToCheckboxes);
      valueMin.addEventListener('input', applyTextFieldsToSliders);
      valueMax.addEventListener('input', applyTextFieldsToSliders);
      valueMin.addEventListener('change', onTextFieldChange);
      valueMax.addEventListener('change', onTextFieldChange);
    };

    const initCollapsibleFilterLists = (currentRoot) => {
      const containers = Array.from(currentRoot.querySelectorAll('[data-collapsible-filter-list]'));
      if (containers.length === 0) return;

      containers.forEach((container) => {
        if (!(container instanceof HTMLElement)) return;
        const list = container.querySelector('[data-filter-list]');
        const toggle = container.parentElement?.querySelector('[data-collapsible-filter-toggle]');
        if (!list || !(toggle instanceof HTMLButtonElement)) return;
        if (list.classList.contains('sf-list--hidden')) {
          toggle.hidden = true;
          container.style.maxHeight = '';
          container.classList.remove('is-collapsed', 'is-expanded', 'has-toggle');
          return;
        }

        const collapsedHeight = (() => {
          const raw = container.getAttribute('data-collapsible-height');
          const n = raw ? Number(raw) : 190;
          return Number.isFinite(n) && n > 0 ? n : 190;
        })();

        container.style.maxHeight = '';
        container.classList.remove('is-collapsed', 'is-expanded', 'has-toggle');
        toggle.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        toggle.textContent = window.vhpT('listing.see_more');

        const fullHeight = container.scrollHeight;
        if (fullHeight <= collapsedHeight + 8) return;

        container.classList.add('has-toggle', 'is-collapsed');
        container.style.maxHeight = `${collapsedHeight}px`;
        toggle.hidden = false;

        if (!toggle.dataset.bound) {
          toggle.dataset.bound = '1';
          toggle.addEventListener('click', () => {
            const isExpanded = container.classList.contains('is-expanded');

            if (isExpanded) {
              container.style.maxHeight = `${container.scrollHeight}px`;
              void container.offsetHeight;
              container.style.maxHeight = `${collapsedHeight}px`;
              container.classList.remove('is-expanded');
              container.classList.add('is-collapsed');
              toggle.setAttribute('aria-expanded', 'false');
              toggle.textContent = window.vhpT('listing.see_more');
            } else {
              container.style.maxHeight = `${container.scrollHeight}px`;
              container.classList.add('is-expanded');
              container.classList.remove('is-collapsed');
              toggle.setAttribute('aria-expanded', 'true');
              toggle.textContent = window.vhpT('listing.see_less');

              const onEnd = (e) => {
                if (e.propertyName !== 'max-height') return;
                container.removeEventListener('transitionend', onEnd);
                if (container.classList.contains('is-expanded')) {
                  container.style.maxHeight = 'none';
                }
              };
              container.addEventListener('transitionend', onEnd);
            }
          });
        }
      });
    };

    const bindSectionEvents = () => {
      const currentRoot = document.querySelector(sectionSelector);
      if (!currentRoot) return;

      const mobileToggleBtn = currentRoot.querySelector('[data-mobile-filters-toggle]');
      const sidebar = currentRoot.querySelector('#sidebarFilters');
      // The app column is phone-width at every viewport, so key off the toggle being shown, not the viewport.
      const isMobileViewport = () => !!mobileToggleBtn && getComputedStyle(mobileToggleBtn).display !== 'none';
      const currentForm = currentRoot.querySelector(`#vhpCollectionFilters-${sectionId}`);

      const bindMobileStickyButton = () => {
        if (!mobileToggleBtn || !(mobileToggleBtn instanceof HTMLElement)) return;
        if (mobileToggleBtn.dataset.stickyBound === '1') return;
        mobileToggleBtn.dataset.stickyBound = '1';

        const sentinel = document.createElement('div');
        sentinel.className = 'vhp-mobile-filters-sentinel';
        sentinel.setAttribute('aria-hidden', 'true');
        sentinel.style.cssText = 'position:relative;height:1px;width:1px;margin:0;padding:0;';
        mobileToggleBtn.parentElement?.insertBefore(sentinel, mobileToggleBtn);

        const stickyTop = 78;
        const observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            const isStuck = !!entry && entry.isIntersecting === false;
            mobileToggleBtn.classList.toggle('is-sticky', isStuck);
          },
          { root: null, threshold: [0], rootMargin: `-${stickyTop}px 0px 0px 0px` }
        );

        observer.observe(sentinel);
      };

      const setMobileFiltersOpen = (open) => {
        if (!sidebar || !mobileToggleBtn) return;
        const stateChanged = sidebar.classList.contains('mobile-open') !== open;

        sidebar.classList.toggle('mobile-open', open);
        mobileToggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        mobileToggleBtn.classList.toggle('is-open', open);
        const mobileFiltersLabel = mobileToggleBtn.querySelector('.vhp-mobile-filters-btn__label');
        if (mobileFiltersLabel) {
          mobileFiltersLabel.textContent = open ? window.vhpT('listing.close_filters') : window.vhpT('listing.filters');
        }
        try {
          sessionStorage.setItem(mobileFiltersKey, open ? '1' : '0');
        } catch (_) {}

        if (!isMobileViewport()) {
          sidebar.style.maxHeight = '';
          return;
        }

        if (!stateChanged) return;

        const headerOffset = 96;
        const top = currentRoot.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

        if (open) {
          sidebar.style.maxHeight = '0px';
          void sidebar.offsetHeight;
          sidebar.style.maxHeight = `${sidebar.scrollHeight}px`;
        } else {
          sidebar.style.maxHeight = `${sidebar.scrollHeight}px`;
          void sidebar.offsetHeight;
          sidebar.style.maxHeight = '0px';
        }
      };

      if (mode === 'collection' && currentForm) {
        currentForm.querySelectorAll('.sf-input').forEach((input) => {
          if (input.dataset.bound === '1') return;
          input.dataset.bound = '1';
          input.addEventListener('change', () => replaceSectionFromUrl(buildUrlFromForm(currentForm)));
        });

        currentForm.querySelectorAll('.sf-range-input').forEach((input) => {
          if (input.dataset.bound === '1') return;
          input.dataset.bound = '1';
          input.addEventListener('change', () => replaceSectionFromUrl(buildUrlFromForm(currentForm)));
        });

        currentRoot.querySelectorAll('[data-filter-section]').forEach((filterSection) => {
          if (filterSection.querySelector('.sf-range-group')) return;
          if (filterSection.dataset.numericRangeBuilt === '1') return;
          filterSection.dataset.numericRangeBuilt = '1';
          buildNumericRangeFilter(filterSection);
        });
      }

      initCollapsibleFilterLists(currentRoot);

      if (mobileToggleBtn && sidebar) {
        if (mobileToggleBtn.dataset.clickBound !== '1') {
          mobileToggleBtn.dataset.clickBound = '1';
          bindMobileStickyButton();
          mobileToggleBtn.addEventListener('click', () => {
            const isOpen = sidebar.classList.contains('mobile-open');
            setMobileFiltersOpen(!isOpen);
          });

          sidebar.addEventListener('transitionend', (e) => {
            if (e.propertyName !== 'max-height') return;
            if (!isMobileViewport()) return;
            if (sidebar.classList.contains('mobile-open')) {
              sidebar.style.maxHeight = 'none';
            }
          });

          window.addEventListener(
            'resize',
            () => {
              if (!sidebar.classList.contains('mobile-open')) return;
              if (!isMobileViewport()) {
                sidebar.style.maxHeight = '';
                return;
              }
              if (sidebar.style.maxHeight === 'none') return;
              sidebar.style.maxHeight = `${sidebar.scrollHeight}px`;
            },
            { passive: true }
          );
        }

        const savedOpen = (() => {
          try {
            return sessionStorage.getItem(mobileFiltersKey) === '1';
          } catch (_) {
            return false;
          }
        })();

        setMobileFiltersOpen(savedOpen);
      }

      currentRoot.querySelectorAll('[data-clear-url]').forEach((btn) => {
        if (btn.dataset.bound === '1') return;
        btn.dataset.bound = '1';
        btn.addEventListener('click', () => {
          const clearUrl = btn.getAttribute('data-clear-url');
          if (!clearUrl) return;
          if (mode === 'blog') {
            window.location.href = clearUrl;
            return;
          }
          replaceSectionFromUrl(new URL(clearUrl, window.location.origin));
        });
      });

      currentRoot.querySelectorAll('[data-clear-all]').forEach((btn) => {
        if (btn.dataset.bound === '1') return;
        btn.dataset.bound = '1';
        btn.addEventListener('click', () => {
          const url = btn.getAttribute('data-url') || listingBaseUrl;
          if (mode === 'blog') {
            window.location.href = url;
            return;
          }
          replaceSectionFromUrl(new URL(url, window.location.origin));
        });
      });

      const sortSelect = currentRoot.querySelector('[data-sort-select]');
      if (sortSelect && currentForm && mode === 'collection') {
        if (sortSelect.dataset.bound !== '1') {
          sortSelect.dataset.bound = '1';
          sortSelect.addEventListener('change', () => {
            const sortInput = currentForm.querySelector('input[name="sort_by"]');
            if (sortInput) {
              sortInput.value = sortSelect.value;
            }
            replaceSectionFromUrl(buildUrlFromForm(currentForm));
          });
        }
      }

      const loadMoreBtn = currentRoot.querySelector('#loadMoreBtn');
      if (loadMoreBtn && loadMoreBtn.dataset.bound !== '1') {
        loadMoreBtn.dataset.bound = '1';
        loadMoreBtn.addEventListener('click', () => {
          appendNextPage(loadMoreBtn.getAttribute('data-next-url'));
        });
      }

      const grid = currentRoot.querySelector('#recipeGrid');
      const gridBtn = currentRoot.querySelector('#gridViewBtn');
      const listBtn = currentRoot.querySelector('#listViewBtn');
      if (grid && gridBtn && listBtn) {
        if (gridBtn.dataset.bound !== '1') {
          gridBtn.dataset.bound = '1';
          gridBtn.addEventListener('click', () => {
            grid.classList.remove('list-view');
            gridBtn.classList.add('active');
            listBtn.classList.remove('active');
            sessionStorage.setItem(viewKey, 'grid');
          });
        }
        if (listBtn.dataset.bound !== '1') {
          listBtn.dataset.bound = '1';
          listBtn.addEventListener('click', () => {
            grid.classList.add('list-view');
            listBtn.classList.add('active');
            gridBtn.classList.remove('active');
            sessionStorage.setItem(viewKey, 'list');
          });
        }
      }

      if (typeof window.initVhpCollectionReels === 'function') {
        window.initVhpCollectionReels(currentRoot);
      }

      if (typeof window.vhpRecipeCardVideoAutoplayScan === 'function') {
        window.vhpRecipeCardVideoAutoplayScan();
      }
    };

    bindSectionEvents();
    applySavedView();
  };

  window.initVhpListingSection = initVhpListingSection;
})();
